import type {
  FinalRecommendationResponse,
  RecommendationResponse,
} from "./types";

/**
 * POST the captured camera burst to `/api/recommend` and return the parsed
 * recommendation. Throws an `Error` with an actionable message on any network,
 * non-2xx or non-JSON failure (surfaced to the operator on the kiosk).
 */
export async function postRecommendOrThrow(
  apiBase: string,
  formData: FormData,
): Promise<RecommendationResponse> {
  const url = `${apiBase.replace(/\/$/, "")}/api/recommend`;
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", body: formData });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Cannot reach recommendation API (${msg}). Request: POST ${url}. Check REACT_APP_ML_API_URL, that the ML server is running, and that this device can reach that host (firewall / VPN / localhost vs LAN IP).`,
    );
  }

  const rawText = await response.text();
  let payload: unknown;
  try {
    payload = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error(
      `Recommendation API returned non-JSON (HTTP ${response.status} ${response.statusText}). URL: ${url}. Body starts: ${rawText.slice(0, 280)}`,
    );
  }

  const obj = payload as { error?: string };
  if (!response.ok) {
    const serverMsg =
      typeof obj.error === "string" && obj.error.trim()
        ? obj.error.trim()
        : rawText.slice(0, 400);
    throw new Error(
      `Recommendation API error (HTTP ${response.status} ${response.statusText}): ${serverMsg}. URL: ${url}`,
    );
  }

  return payload as RecommendationResponse;
}

/**
 * POST the emotion + question answers to `/api/finalize-recommendation` and
 * return the final ranked picks. Throws an `Error` with context on any
 * network, non-2xx or non-JSON failure.
 */
export async function postFinalizeOrThrow(
  apiBase: string,
  payload: {
    emotion: string;
    guest_id?: string | null;
    guest_mode?: string | null;
    lang?: string;
    machine_menu: string[];
    /** Backend machine-products payload; when present the service derives the
     *  menu and drink attributes from it (machine_menu can be empty). */
    inventory?: unknown[];
    /** False excludes alcoholic drinks from the recommendation candidates. */
    allow_alcoholic?: boolean;
    question_answers: Array<{ questionId: string; answer: string }>;
  },
): Promise<FinalRecommendationResponse> {
  const url = `${apiBase.replace(/\/$/, "")}/api/finalize-recommendation`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Cannot reach finalize API (${msg}). Request: POST ${url}.`,
    );
  }

  const rawText = await response.text();
  let parsed: unknown;
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error(
      `Finalize API returned non-JSON (HTTP ${response.status} ${response.statusText}).`,
    );
  }
  const obj = parsed as { error?: string };
  if (!response.ok) {
    throw new Error(
      `Finalize API error (HTTP ${response.status} ${response.statusText}): ${obj.error || rawText.slice(0, 300)}`,
    );
  }
  return parsed as FinalRecommendationResponse;
}
